import { useEffect, useState } from "react";
import {
  WhatsappLogo, DeviceMobile, ShareNetwork, RadioButton, ArrowRight, Play,
  CaretRight, CaretLeft, FacebookLogo, InstagramLogo, TiktokLogo, LinkedinLogo, Globe,
  ShieldCheck, AirplaneTilt, PiggyBank, Briefcase, YoutubeLogo, XLogo, List, X,
} from "@phosphor-icons/react";
import CountrySelector from "../../components/web/CountrySelector";
import { BrandCurve, BrandGlow } from "../../components/web/BrandLines";
import CountryInterestModal from "../../components/web/CountryInterestModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PINK = "#E60059";
const BLUE = "#025582";
const NAVY = "#042E45";
const INK = "#042E45";
const CREAM = "#F8F5F2";
const TEAL = "#025582";
const GRAY = "#4A4A4A";

const openWa = (l) => { if (l) window.open(l, "_blank", "noopener,noreferrer"); };

function Head({ title, desc, light }) {
  return (
    <div className="grid md:grid-cols-2 gap-4 md:gap-10 items-end mb-10">
      <h2 className="text-3xl md:text-[44px] font-extrabold leading-[1.05]" style={{ color: light ? "#fff" : INK }}>{title}</h2>
      {desc && <p className="text-base md:text-lg" style={{ color: light ? "rgba(255,255,255,.82)" : GRAY }}>{desc}</p>}
    </div>
  );
}

function EcoCard({ img, title, desc }) {
  return (
    <div data-testid={`eco-card-${title}`}>
      <div className="h-52 rounded-2xl overflow-hidden mb-4">
        <img src={img} alt={title} loading="lazy" className="w-full h-full object-cover" />
      </div>
      <h3 className="text-xl font-extrabold pb-2 mb-2" style={{ color: INK, borderBottom: "1px solid #e7e3df" }}>{title}</h3>
      <p className="text-sm" style={{ color: GRAY }}>{desc}</p>
    </div>
  );
}

function SocialRow({ social, color }) {
  const items = [
    ["facebook", FacebookLogo], ["instagram", InstagramLogo], ["tiktok", TiktokLogo],
    ["linkedin", LinkedinLogo], ["youtube", YoutubeLogo], ["x", XLogo],
  ].filter(([k]) => social?.[k]);
  if (!items.length) return null;
  return (
    <div className="flex items-center gap-3" style={{ color }}>
      {items.map(([k, Icon]) => (
        <a key={k} href={social[k]} target="_blank" rel="noopener noreferrer" data-testid={`social-${k}`} className="hover:opacity-70"><Icon size={20} weight="fill" /></a>
      ))}
    </div>
  );
}

export default function HomeV2() {
  const [data, setData] = useState(null);
  const [slide, setSlide] = useState(0);
  const [showCountry, setShowCountry] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    document.title = "Héroes Migrantes — Tus dos mundos, más cerca";
    fetch(`${BACKEND}/api/web/content`).then((r) => r.json()).then(setData).catch(() => setData({}));
  }, []);

  const wa = data?.wa || {}, img = data?.images || {}, modules = data?.modules || {}, editorial = data?.editorial || [];
  const T = data?.texts || {}, social = data?.social || {};
  const tx = (k, fb) => (T[k] ?? fb);

  const moduleInterest = async (k) => {
    try {
      await fetch(`${BACKEND}/api/web/module-interest`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ module: k }),
      });
    } catch { /* noop */ }
    openWa(wa[k]);
  };

  const shareHM = async () => {    const sd = { title: "Héroes Migrantes", text: "Tus dos mundos, más cerca. Conocé Héroes Migrantes.", url: window.location.origin };
    if (navigator.share) { try { await navigator.share(sd); return; } catch { /* */ } }
    window.open(`https://wa.me/?text=${encodeURIComponent(sd.text + " " + sd.url)}`, "_blank", "noopener");
  };

  const nuestra = [
    { title: tx("home.radio_title", "Radio"), desc: tx("home.radio_desc", "Las radios de tu tierra, para escuchar desde donde estés."), img: img.radio, cta: tx("home.radio_cta", "Escuchá") },
    { title: tx("home.buenas_title", "Buenas Noticias"), desc: tx("home.buenas_desc", "Las buenas noticias de tu pueblo y de tu tierra: logros, tradiciones y comunidad."), img: img.buenas, cta: tx("home.buenas_cta", "Leer más") },
    { title: tx("home.asi_title", "Así se Vivió"), desc: tx("home.asi_desc", "Fotos de las fiestas, ferias y celebraciones de tu tierra, para revivirlas aunque estés lejos."), img: img.asi, cta: tx("home.asi_cta", "Ver fotos") },
  ];
  const comunidad = [
    { title: tx("home.historias_title", "Historias del Migrante"), desc: tx("home.historias_desc", "Historias reales de otros migrantes: cómo viven, qué extrañan y cómo construyen su vida lejos de casa."), img: img.historias, cta: tx("home.historias_cta", "Conocé una historia") },
    { title: tx("home.eventos_title", "Eventos por Venir"), desc: tx("home.eventos_desc", "Los próximos eventos de tu comunidad migrante cerca de donde vivís: torneos, fiestas, actividades culturales y más."), img: img.eventos, cta: tx("home.eventos_cta", "Mirá qué se viene") },
    { title: tx("home.mercadito_title", "Mercadito"), desc: tx("home.mercadito_desc", "Comprá y vendé productos de tu tierra con otros migrantes en donde vivís."), img: img.mercadito, cta: tx("home.mercadito_cta", "Explorá") },
  ];
  const stBadge = (s) => s === "EN_DESARROLLO" ? { t: "En desarrollo", c: "#1f9d6b" } : { t: "En planificación", c: GRAY };

  return (
    <div data-testid="home-v2" style={{ fontFamily: "'Poppins', system-ui, sans-serif", color: INK, background: "#fff" }}>
      {/* HEADER */}
      <header className="sticky top-0 z-50 backdrop-blur" style={{ background: "rgba(255,255,255,.92)", borderBottom: "1px solid #eee" }}>
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center" data-testid="web-logo">
            <img src={data?.logo_url || "/hm-logo.png"} alt="Héroes Migrantes" className="h-6 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-7 text-sm font-semibold">
            <a href="#top" className="hover:opacity-70">Inicio</a>
            <a href="#que-es" className="hover:opacity-70">Qué es Héroes Migrantes</a>
            <a href="#ecosistema" className="hover:opacity-70">Ecosistema</a>
            <a href="#participa" className="hover:opacity-70">Participá</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <CountrySelector onRequestCountry={() => setShowCountry(true)} />
            <button onClick={() => setMenuOpen(true)} data-testid="mobile-menu-open" aria-label="Abrir menú" className="md:hidden w-10 h-10 flex items-center justify-center rounded-full" style={{ color: INK }}>
              <List size={24} weight="bold" />
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE NAV OVERLAY */}
      {menuOpen && (
        <div className="fixed inset-0 z-[90] md:hidden flex flex-col" style={{ background: NAVY }} data-testid="mobile-nav-overlay">
          <div className="flex items-center justify-between px-5 h-16">
            <span className="font-extrabold text-white">Héroes Migrantes</span>
            <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" aria-label="Cerrar menú" className="w-10 h-10 flex items-center justify-center rounded-full text-white"><X size={24} weight="bold" /></button>
          </div>
          <nav className="flex flex-col gap-1 px-5 mt-6 text-2xl font-extrabold text-white">
            {[["#top", "Inicio"], ["#que-es", "Qué es"], ["#ecosistema", "Ecosistema"], ["/aspirantes", "Participá"]].map(([href, label]) => (
              <a key={label} href={href} onClick={() => setMenuOpen(false)} className="py-3 border-b" style={{ borderColor: "rgba(255,255,255,.12)" }}>{label}</a>
            ))}
          </nav>
          <div className="px-5 mt-8">
            <button onClick={() => { setMenuOpen(false); setShowCountry(true); }} className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-base font-bold" style={{ background: "#fff", color: NAVY }}>
              <Globe size={18} weight="bold" /> ¿HM en tu país?
            </button>
          </div>
          <div className="mt-auto px-5 pb-10"><SocialRow social={social} color="rgba(255,255,255,.85)" /></div>
        </div>
      )}

      {/* BLOQUE 1 · HERO */}
      <section id="top" className="relative overflow-hidden" style={{ background: CREAM }}>
        <div className="max-w-7xl mx-auto px-5 pt-8 pb-14 md:pt-16 md:pb-24 grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div className="order-2 md:order-1">
            <h1 className="font-extrabold leading-[0.92] mb-7 text-5xl sm:text-6xl lg:text-[82px]">
              <span style={{ color: INK }}>{tx("home.hero_l1", "Tu tierra.")}</span><br />
              <span style={{ color: INK }}>{tx("home.hero_l2", "Tu gente.")}</span><br />
              <span style={{ color: PINK }}>{tx("home.hero_l3", "Tus dos mundos.")}</span>
            </h1>
            <p className="text-base md:text-lg mb-8 max-w-md" style={{ color: GRAY }}>{tx("home.hero_desc", "Tus dos mundos, más cerca. Hay un lugar del que venís y otro donde estás construyendo tu vida. Una comunidad para seguir cerca de tu tierra, encontrar a tu gente, descubrir cosas que pueden ayudarte y participar, estés donde estés.")}</p>
            <button onClick={() => setShowCountry(true)} data-testid="hero-cta" className="inline-flex items-center rounded-full px-6 py-3.5 text-base font-bold text-white transition-transform hover:-translate-y-0.5" style={{ background: BLUE }}>{tx("home.hero_cta", "Descubrí Héroes Migrantes")}</button>
          </div>
          <div className="relative order-1 md:order-2 -mx-5 md:mx-0 mb-8 md:mb-0">
            <img src={img.hero} alt="Familia Héroes Migrantes" className="w-full h-[300px] md:h-[540px] object-cover rounded-none md:rounded-[28px]" />
          </div>
        </div>
      </section>
      <BrandCurve variant="navyStripe" bg={CREAM} />

      {/* BLOQUE 2 · ESTO ES HÉROES MIGRANTES */}
      <section id="que-es" className="py-16 md:py-20" style={{ background: CREAM }}>
        <div id="ecosistema" className="max-w-7xl mx-auto px-5 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-4xl md:text-6xl font-extrabold leading-[1.05] mb-5" style={{ color: INK, whiteSpace: "pre-line" }}>{tx("home.esto_title", "Esto es\nHéroes Migrantes")}</h2>
            <p className="text-lg md:text-xl mb-4" style={{ color: INK }}>{tx("home.esto_lead", "Mucho más que estar cerca.")}</p>
            <p className="text-base md:text-lg mb-3" style={{ color: GRAY }}>{tx("home.esto_p1", "Héroes Migrantes es un ecosistema creado para acompañarte en tus dos mundos.")}</p>
            <p className="text-base md:text-lg mb-3" style={{ color: GRAY }}>{tx("home.esto_p2", "Un lugar donde podés mantenerte conectado con tu tierra, descubrir historias y actividades de tu comunidad, encontrar cosas que pueden ayudarte y también participar.")}</p>
            <p className="text-base md:text-lg" style={{ color: GRAY }}>{tx("home.esto_p3", "Empezamos con Guatemala y estamos creciendo para conectar a más comunidades latinoamericanas.")}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="rounded-3xl overflow-hidden h-52 md:h-64"><img src={img.eco1} alt="Guatemala" loading="lazy" className="w-full h-full object-cover" /></div>
              <div className="rounded-3xl overflow-hidden h-40 md:h-52"><img src={img.asi} alt="" loading="lazy" className="w-full h-full object-cover" /></div>
            </div>
            <div className="space-y-4 md:pt-10">
              <div className="rounded-3xl overflow-hidden h-40 md:h-52"><img src={img.eco2} alt="" loading="lazy" className="w-full h-full object-cover" /></div>
              <div className="rounded-3xl overflow-hidden h-52 md:h-64"><img src={img.historias} alt="" loading="lazy" className="w-full h-full object-cover" /></div>
            </div>
          </div>
        </div>
      </section>
      <BrandCurve variant="pinkStripe" bg={CREAM} to="#fff" />

      {/* NUESTRA TIERRA */}
      <section className="py-16 md:py-20" style={{ background: "#fff" }}>
        <div className="max-w-7xl mx-auto px-5">
          <Head title={tx("home.nuestra_title", "Nuestra tierra")} desc={tx("home.nuestra_desc", "Para seguir cerca de Guatemala, sus lugares, su cultura y lo que está pasando.")} />
          <div className="grid md:grid-cols-3 gap-8">
            {nuestra.map((c) => <EcoCard key={c.title} {...c} />)}
          </div>
        </div>
      </section>

      {/* COMUNIDAD MIGRANTE */}
      <section className="py-16 md:py-20" style={{ background: "#fff" }}>
        <div className="max-w-7xl mx-auto px-5">
          <Head title={tx("home.comunidad_title", "Comunidad Migrante")} desc={tx("home.comunidad_desc", "Lo que te conecta con la comunidad que estás construyendo donde vivís.")} />
          <div className="grid md:grid-cols-3 gap-8">
            {comunidad.map((c) => <EcoCard key={c.title} {...c} />)}
          </div>
        </div>
      </section>
      <BrandCurve variant="toNavy" bg="#fff" />

      {/* BLOQUE 3 · MIRÁ LO QUE ESTÁ PASANDO */}
      <section className="relative overflow-hidden py-16 md:py-20" style={{ background: NAVY }}>
        <BrandGlow className="-right-24 -top-10 w-[520px] md:w-[760px] opacity-[.18]" />
        <div className="relative max-w-7xl mx-auto px-5">
          <div className="grid md:grid-cols-2 gap-4 md:gap-10 items-start mb-10">
            <h2 className="text-4xl md:text-5xl lg:text-[52px] font-extrabold leading-[1.05] text-white">{tx("home.mira_title", "Mirá lo que está pasando en Héroes Migrantes")}</h2>
            <div className="flex flex-col gap-5 md:items-end">
              <p className="text-lg md:text-xl" style={{ color: "rgba(255,255,255,.82)" }}>{tx("home.mira_desc", "Historias, voces, noticias y encuentros que mantienen cerca tus dos mundos.")}</p>
              {editorial.length > 1 && (
                <div className="flex gap-3">
                  <button onClick={() => setSlide((s) => (s - 1 + editorial.length) % editorial.length)} data-testid="carousel-prev" className="w-11 h-11 rounded-full flex items-center justify-center text-white" style={{ background: BLUE }}><CaretLeft size={18} weight="bold" /></button>
                  <button onClick={() => setSlide((s) => (s + 1) % editorial.length)} data-testid="carousel-next" className="w-11 h-11 rounded-full flex items-center justify-center text-white" style={{ background: BLUE }}><CaretRight size={18} weight="bold" /></button>
                </div>
              )}
            </div>
          </div>
          {editorial.length > 0 && (
            <div className="overflow-hidden">
              <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(calc(-${slide} * (100% - 3rem)))` }}>
                {editorial.map((e, i) => (
                  <div key={i} data-testid="editorial-card" className="shrink-0 w-full md:w-[calc(100%-3rem)] pr-0 md:pr-6">
                    <div className="grid md:grid-cols-[46%_1fr] rounded-3xl overflow-hidden bg-white">
                      <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[360px]">
                        <img src={e.media_url} alt={e.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                        {e.media_type === "video" && <span className="absolute inset-0 flex items-center justify-center"><span className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center"><Play size={26} weight="fill" style={{ color: NAVY }} /></span></span>}
                      </div>
                      <div className="p-8 md:p-12 flex flex-col justify-center">
                        <span className="self-start text-sm font-extrabold px-4 py-2 rounded-full mb-5" style={{ background: PINK, color: "#fff" }}>{e.title}</span>
                        <p className="text-base md:text-lg mb-6 max-w-lg" style={{ color: GRAY }}>{e.text}</p>
                        {e.cta_href && (
                          <button onClick={() => window.open(e.cta_href, "_blank", "noopener,noreferrer")} className="inline-flex items-center gap-2 text-base font-extrabold" style={{ color: TEAL }}>{e.cta_label || "Ver más"} <ArrowRight size={16} weight="bold" /></button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <BrandCurve variant="fromNavy" bg={CREAM} />

      {/* BLOQUE 4 · Y ESTO APENAS COMIENZA */}
      <section className="py-16 md:py-20" style={{ background: CREAM }}>
        <div className="max-w-7xl mx-auto px-5">
          <Head title={tx("home.yesto_title", "Y esto apenas comienza")} desc={tx("home.yesto_desc", "Estamos construyendo nuevas formas de acompañarte en decisiones importantes de tus dos mundos.")} />
          <div className="relative mt-4">
            {/* Línea central del recorrido */}
            <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[2px]" style={{ background: NAVY }} />
            <div className="flex flex-col gap-16 md:gap-24">
              {["todo_en_orden", "plan_de_regreso", "cuida_tu_bolsillo", "laboral"].map((k, i) => {
                const m = modules[k]; if (!m) return null; const b = stBadge(m.state);
                const mi = { todo_en_orden: img.todo, plan_de_regreso: img.eco2, cuida_tu_bolsillo: img.buenas, laboral: img.radio }[k];
                const Icon = { todo_en_orden: ShieldCheck, plan_de_regreso: AirplaneTilt, cuida_tu_bolsillo: PiggyBank, laboral: Briefcase }[k];
                const imgLeft = i % 2 === 0;
                const ImageBlock = (
                  <div className="rounded-3xl overflow-hidden h-56 md:h-72"><img src={mi} alt={m.title} loading="lazy" className="w-full h-full object-cover" /></div>
                );
                const TextBlock = (
                  <div className={imgLeft ? "md:pl-4" : "md:pr-4 md:text-right"}>
                    <span className="text-sm font-extrabold" style={{ color: b.c }}>{b.t}</span>
                    <h3 className="text-2xl md:text-3xl font-extrabold mt-1 mb-3" style={{ color: INK }}>{m.title}</h3>
                    <p className="text-base mb-4" style={{ color: GRAY }}>{m.desc}</p>
                    {m.cta && m.state === "EN_DESARROLLO" && (
                      <button onClick={() => moduleInterest(k)} data-testid={`module-cta-${k}`} className={`inline-flex items-center gap-1.5 text-base font-extrabold ${imgLeft ? "" : "md:flex-row-reverse"}`} style={{ color: TEAL }}>{m.cta_label || "Me interesa"} <ArrowRight size={16} weight="bold" /></button>
                    )}
                  </div>
                );
                return (
                  <div key={k} data-testid={`module-${k}`} className="relative grid md:grid-cols-[1fr_auto_1fr] gap-5 md:gap-10 items-center" style={{ opacity: m.state === "EN_DESARROLLO" ? 1 : 0.55 }}>
                    <div className={imgLeft ? "order-1" : "order-2 md:order-1"}>{imgLeft ? ImageBlock : TextBlock}</div>
                    <div className="hidden md:flex order-2 relative z-10">
                      <span className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md" style={{ background: m.state === "EN_DESARROLLO" ? PINK : NAVY, border: `4px solid ${CREAM}` }}><Icon size={26} weight="fill" style={{ color: "#fff" }} /></span>
                    </div>
                    <div className={imgLeft ? "order-3" : "order-1 md:order-3"}>{imgLeft ? TextBlock : ImageBlock}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <BrandCurve variant="toNavy" bg={CREAM} />

      {/* BLOQUE 5 · TAMBIÉN LO CONSTRUÍS VOS */}
      <section id="participa" className="relative overflow-hidden py-16 md:py-20" style={{ background: NAVY }}>
        <BrandGlow className="-left-40 -bottom-24 w-[520px] md:w-[720px] opacity-[.18] -scale-x-100" />
        <div className="relative max-w-7xl mx-auto px-5">
          <Head light title={tx("home.participa_title", "Héroes Migrantes también lo construís vos")} desc={tx("home.participa_desc", "Cada aporte hace la comunidad más grande.")} />
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { Icon: ShareNetwork, t: tx("home.participa_c1_title", "Compartí Héroes Migrantes"), d: tx("home.participa_c1_desc", "Ayudá a que más personas encuentren su comunidad."), cta: tx("home.participa_c1_cta", "Compartir HM"), on: shareHM, tid: "cta-share-hm" },
              { Icon: RadioButton, t: tx("home.participa_c2_title", "Sugerí una radio"), d: tx("home.participa_c2_desc", "¿Conocés una emisora? Pasanos el enlace y HM decide si la suma."), cta: tx("home.participa_c2_cta", "Sugerir una radio"), on: () => openWa(wa.general), tid: "cta-suggest-radio" },
              { Icon: Globe, t: tx("home.participa_c3_title", "Sumate con lo que hacés"), d: tx("home.participa_c3_desc", "Negocios, radios y organizaciones: una sola puerta para participar."), cta: tx("home.participa_c3_cta", "Quiero participar"), on: () => openWa(wa.sumate), tid: "cta-sumate" },
            ].map((x) => (
              <div key={x.t} className="rounded-3xl p-7" style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)" }}>
                <h3 className="text-xl font-extrabold text-white pb-3 mb-3" style={{ borderBottom: "1px solid rgba(255,255,255,.14)" }}>{x.t}</h3>
                <p className="text-sm mb-5" style={{ color: "rgba(255,255,255,.7)" }}>{x.d}</p>
                <button onClick={x.on} data-testid={x.tid} className="inline-flex items-center gap-1.5 text-sm font-bold text-white">{x.cta} <ArrowRight size={14} weight="bold" /></button>
              </div>
            ))}
          </div>
          <div className="mt-12 rounded-3xl px-7 py-9 md:px-10 flex flex-col md:flex-row md:items-center justify-between gap-6" style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)" }}>
            <div className="md:max-w-xl">
              <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-2">{tx("home.aspbanner_title", "¿Querés involucrarte todavía más?")}</h3>
              <p className="text-base" style={{ color: "rgba(255,255,255,.75)" }}>{tx("home.aspbanner_desc", "Conocé qué significa ser Aspirante a Embajador y el camino para representarlo en tu comunidad.")}</p>
            </div>
            <a href="/aspirantes" data-testid="cta-aspirantes" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-base font-bold text-white shrink-0" style={{ background: BLUE }}>{tx("home.aspbanner_cta", "Quiero conocer el camino")} <ArrowRight size={16} weight="bold" /></a>
          </div>
        </div>
      </section>

      {/* BLOQUE 6 · DONDE ESTÉS */}
      <section className="py-16 md:py-20" style={{ background: "#fff" }}>
        <div className="max-w-7xl mx-auto px-5">
          <Head title={tx("home.donde_title", "Héroes Migrantes, donde estés")} desc={tx("home.donde_desc", "Elegí por dónde seguir.")} />
          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl">
            <div className="rounded-2xl p-6" style={{ background: CREAM }}>
              <span className="inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-3" style={{ background: PINK }}><Globe size={24} weight="bold" style={{ color: "#fff" }} /></span>
              <h3 className="font-extrabold text-lg mb-3 pb-3 border-b" style={{ borderColor: "#e7e3df" }}>{tx("home.donde_web_title", "En la Web")}</h3>
              <p className="text-sm" style={{ color: GRAY }}>{tx("home.donde_web_desc", "Explorá la comunidad desde acá mismo, sin descargar nada.")}</p>
            </div>
            <div className="rounded-2xl p-6" style={{ background: CREAM }}>
              <span className="inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-3" style={{ background: PINK }}><DeviceMobile size={24} weight="bold" style={{ color: "#fff" }} /></span>
              <h3 className="font-extrabold text-lg mb-3 pb-3 border-b" style={{ borderColor: "#e7e3df" }}>{tx("home.donde_app_title", "En la App")}</h3>
              <p className="text-sm mb-4" style={{ color: GRAY }}>{tx("home.donde_app_desc", "Más cerca de tu comunidad, con funciones que la Web no tiene.")}</p>
              <a href={data?.app_url || "#"} target="_blank" rel="noopener noreferrer" data-testid="cta-app" className="inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: TEAL }}>{tx("home.donde_app_cta", "Conocé la App")} <ArrowRight size={14} weight="bold" /></a>
            </div>
            <div className="rounded-2xl p-6" style={{ background: CREAM }}>
              <span className="inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-3" style={{ background: PINK }}><WhatsappLogo size={24} weight="fill" style={{ color: "#fff" }} /></span>
              <h3 className="font-extrabold text-lg mb-3 pb-3 border-b" style={{ borderColor: "#e7e3df" }}>{tx("home.donde_luciana_title", "Con Luciana en WhatsApp")}</h3>
              <p className="text-sm mb-4" style={{ color: GRAY }}>{tx("home.donde_luciana_desc", "Conversá y resolvé tus dudas, a tu ritmo.")}</p>
              <button onClick={() => openWa(wa.luciana)} data-testid="cta-luciana" className="inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: TEAL }}>{tx("home.donde_luciana_cta", "Hablar con Luciana")} <ArrowRight size={14} weight="bold" /></button>
            </div>
            <div className="rounded-2xl p-6" style={{ background: CREAM }}>
              <span className="inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-3" style={{ background: PINK }}><InstagramLogo size={24} weight="bold" style={{ color: "#fff" }} /></span>
              <h3 className="font-extrabold text-lg mb-3 pb-3 border-b" style={{ borderColor: "#e7e3df" }}>{tx("home.donde_redes_title", "En las Redes")}</h3>
              <p className="text-sm mb-3" style={{ color: GRAY }}>{tx("home.donde_redes_desc", "Historias y conexión cotidiana.")}</p>
              <SocialRow social={social} color={INK} />
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: INK }} className="text-white">
        <div className="max-w-7xl mx-auto px-5 py-12">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 text-center md:text-left">
            <div className="flex flex-col items-center md:items-start">
              <img src="/hm-logo-white.png" alt="Héroes Migrantes" className="h-10 w-auto object-contain mb-3" />
              <span className="font-semibold">Héroes Migrantes</span>
              <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,.6)" }}>{tx("home.footer_tagline", "Tus dos mundos, más cerca.")}</p>
            </div>
            <div className="flex justify-center md:justify-end"><SocialRow social={social} color="rgba(255,255,255,.85)" /></div>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-6 mt-8 text-center" style={{ borderTop: "1px solid rgba(255,255,255,.12)", color: "rgba(255,255,255,.55)" }}>
            <span className="text-xs">© 2026 Héroes Migrantes</span>
            <span className="text-xs">Privacidad · Términos y condiciones · <a href="/admin" className="hover:underline">Acceso equipo</a></span>
          </div>
        </div>
      </footer>
      <CountryInterestModal open={showCountry} onClose={() => setShowCountry(false)} waLink={wa.pais} />
    </div>
  );
}
